import * as React from "react";
import { useState } from "react";
import { usePathname } from "../router";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "../dialog";
import { Button } from "../button";
import { Input } from "../input";
import { Textarea } from "../textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../select";
import { FormShell, FormField } from "./form-shell";
import { apiCall } from "../api/client";
import { bugReport } from "../api/contracts/bug-report";

/**
 * Cross-product bug reporting (docs/bug-report/PRD.md §5) — the web-shell's
 * report-a-bug dialog. Fully controlled (no built-in trigger) so a host chrome
 * surface (e.g. the user menu) owns the open state and just flips it on from a
 * menu item. Submits through the `bug-report` contract, whose route stamps
 * product/version/platform/context server-side (PRD §6) — this form only
 * collects what the user types, and says exactly what else goes out.
 */

const SEVERITY_UNSET = "unspecified" as const;
const SEVERITIES = [
  ["low", "Low"],
  ["medium", "Medium"],
  ["high", "High"],
] as const;

export interface BugReportDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function BugReportDialog({ open, onOpenChange }: BugReportDialogProps) {
  const pathname = usePathname();
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [severity, setSeverity] = useState<string>(SEVERITY_UNSET);
  const [contactEmail, setContactEmail] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [receiptId, setReceiptId] = useState<string | null>(null);

  function reset() {
    setTitle("");
    setDescription("");
    setSeverity(SEVERITY_UNSET);
    setContactEmail("");
    setError(null);
    setReceiptId(null);
  }

  function handleOpenChange(next: boolean) {
    if (!next) reset();
    onOpenChange(next);
  }

  async function submit(e: React.FormEvent<HTMLFormElement>) {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      const result = await apiCall(bugReport.submit, {
        body: {
          title: title.trim(),
          description: description.trim() || undefined,
          severity:
            severity === SEVERITY_UNSET
              ? undefined
              : (severity as "low" | "medium" | "high"),
          contact_email: contactEmail.trim() || undefined,
          route: pathname || undefined,
        },
      });
      if (!result.ok) {
        setError(result.error ?? "Couldn't send the report — please try again.");
        return;
      }
      setReceiptId(result.id ?? "");
    } catch {
      setError("Couldn't send the report — please try again.");
    } finally {
      setBusy(false);
    }
  }

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Report a bug</DialogTitle>
        </DialogHeader>
        {receiptId !== null ? (
          <div className="flex flex-col gap-4 px-1 pb-2">
            <p className="type-body text-foreground">
              Thanks — receipt {receiptId}
            </p>
            <div className="flex justify-end">
              <Button type="button" onClick={() => handleOpenChange(false)}>
                Done
              </Button>
            </div>
          </div>
        ) : (
          <FormShell
            compact
            onSubmit={submit}
            error={error ?? undefined}
            footer={
              <>
                <Button
                  type="button"
                  variant="ghost"
                  onClick={() => handleOpenChange(false)}
                >
                  Cancel
                </Button>
                <Button type="submit" disabled={busy || title.trim().length === 0}>
                  Send report
                </Button>
              </>
            }
          >
            <FormField label="Title" required>
              <Input
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                placeholder="What went wrong?"
                maxLength={200}
                autoFocus
              />
            </FormField>
            <FormField label="Description" help="Steps to reproduce, what you expected, what happened.">
              <Textarea
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder="Optional — the more detail the better."
                rows={4}
                maxLength={10_000}
              />
            </FormField>
            <FormField label="Severity">
              <Select value={severity} onValueChange={setSeverity}>
                <SelectTrigger>
                  <SelectValue placeholder="Not specified" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value={SEVERITY_UNSET}>Not specified</SelectItem>
                  {SEVERITIES.map(([value, label]) => (
                    <SelectItem key={value} value={value}>
                      {label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </FormField>
            <FormField label="Contact email" help="Optional — only if you want a follow-up.">
              <Input
                type="email"
                value={contactEmail}
                onChange={(e) => setContactEmail(e.target.value)}
                placeholder="you@example.com"
              />
            </FormField>
            <p className="type-subtext text-muted-foreground">
              Sent with your report: product, app version, OS, and the current
              page path.
            </p>
          </FormShell>
        )}
      </DialogContent>
    </Dialog>
  );
}
